import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  marcacionesGetAllConfigsAPI,
  marcacionesGetSingleStatsCoreIdAPI,
  marcacionesGetStatsByServiceAndConfigurationAPI,
  MultiStatsResponse,
  SingleStatsRequest,
  SingleStatsResponse,
  StatsByServiceAndConfigurationRequest,
  ViewConfig,
} from "./marcacionesV2API";
import { LoadingStatus } from "../../common/commonSlice";

export interface MarcacionesState {
  singleStats: SingleStatsResponse | null;
  error: string | null;
  configs: ViewConfig[] | null;
  multiStats: MultiStatsResponse | null;
  currentConfigId: string | null;
  configsLoading: LoadingStatus;
  statsLoading: LoadingStatus;
  startDate: string | null;
  endDate: string | null;
}

const initialState: MarcacionesState = {
  singleStats: null,
  error: null,
  configs: null,
  multiStats: null,
  currentConfigId: null,
  configsLoading: "idle",
  statsLoading: "idle",
  startDate: null,
  endDate: null,
};

export const getConfigs = createAsyncThunk(
  "marcaciones/getConfigs",
  async (_, { rejectWithValue }) => {
    console.log("Get Configs Thunk called");
    try {
      const response = await marcacionesGetAllConfigsAPI();
      if (response.error) {
        return rejectWithValue(response.error);
      }
      return response.configs;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getSingleStats = createAsyncThunk(
  "marcaciones/getSingleStats",
  async (request: SingleStatsRequest, { rejectWithValue }) => {
    try {
      const response = await marcacionesGetSingleStatsCoreIdAPI(request);
      if (response.error) {
        return rejectWithValue(response.error);
      }
      return response.stats;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getMultiStats = createAsyncThunk(
  "marcaciones/getMultiStats",
  async (
    request: StatsByServiceAndConfigurationRequest,
    { rejectWithValue }
  ) => {
    try {
      const response = await marcacionesGetStatsByServiceAndConfigurationAPI(
        request
      );
      if (response.error) {
        return rejectWithValue(response.error);
      }
      return response.stats;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const marcacionesSlicer = createSlice({
  name: "marcaciones",
  initialState,
  reducers: {
    marcacionesSetCurrentConfigId: (state, action) => {
      state.currentConfigId = action.payload;
    },
    marcacionesSetStartDate: (state, action) => {
      state.startDate = action.payload;
    },
    marcacionesSetEndDate: (state, action) => {
      state.endDate = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getConfigs.pending, (state) => {
        state.configsLoading = "pending";
        state.error = null;
      })
      .addCase(getConfigs.fulfilled, (state, action) => {
        state.configs = action.payload;
        state.error = null;
        state.configsLoading = "resolved";
      })
      .addCase(getConfigs.rejected, (state, action) => {
        state.error = action.payload as string;
        state.configsLoading = "rejected";
      })

      .addCase(getSingleStats.fulfilled, (state, action) => {
        state.singleStats = action.payload;
        state.error = null;
        state.statsLoading = "resolved";
      })
      .addCase(getSingleStats.rejected, (state, action) => {
        state.error = action.payload as string;
        state.statsLoading = "rejected";
      })
      .addCase(getMultiStats.pending, (state) => {
        state.statsLoading = "pending";
        state.error = null;
      })
      .addCase(getMultiStats.fulfilled, (state, action) => {
        state.multiStats = action.payload;
        state.statsLoading = "resolved";
        state.error = null;
      })
      .addCase(getMultiStats.rejected, (state, action) => {
        state.multiStats = null;
        state.statsLoading = "rejected";
        state.error = action.payload as string;
      });
  },
});

export const {
  marcacionesSetCurrentConfigId,
  marcacionesSetStartDate,
  marcacionesSetEndDate,
} = marcacionesSlicer.actions;

export const selectMarcacionesState = (state: {
  marcaciones: MarcacionesState;
}) => state.marcaciones;

export const selectMarcacionesSingleStats = (state: {
  marcaciones: MarcacionesState;
}) => state.marcaciones.singleStats;

export const selectMarcacionesError = (state: {
  marcaciones: MarcacionesState;
}) => state.marcaciones.error;

export const selectMarcacionesConfigs = (state: {
  marcaciones: MarcacionesState;
}) => state.marcaciones.configs;

export const selectMarcacionesMultiStats = (state: {
  marcaciones: MarcacionesState;
}) => state.marcaciones.multiStats;

export const selectCurrentConfigId = (state: {
  marcaciones: MarcacionesState;
}) => state.marcaciones.currentConfigId;

export const selectMarcacionesConfigsLoading = (state: {
  marcaciones: MarcacionesState;
}) => state.marcaciones.configsLoading;

export const selectMarcacionesStatsLoading = (state: {
  marcaciones: MarcacionesState;
}) => state.marcaciones.statsLoading;

export const selectMarcacionesStartDate = (state: {
  marcaciones: MarcacionesState;
}) => state.marcaciones.startDate;

export const selectMarcacionesEndDate = (state: {
  marcaciones: MarcacionesState;
}) => state.marcaciones.endDate;

// Export the reducer to be used in the store

export default marcacionesSlicer.reducer;
